import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { BreadCrumb } from 'primereact/breadcrumb';
import { TabView, TabPanel } from 'primereact/tabview';
import { cardQueryKeys, deleteCardRequest, fetchCardsList } from './card-query';
import PCol from 'shared/ui/pcol';
import { translate } from 'i18n/jhipster-language';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import moment from 'moment';
import { confirmPopup } from 'primereact/confirmpopup';
import PRow from 'shared/ui/prow';
import { ROWS_PER_PAGE } from 'app/config/constants';
import { Tag } from 'primereact/tag';
import { isTableSkeletonRow, TABLE_SKELETON_ROW_COUNT, tableActionButtonsSkeleton, tableCellSkeleton, } from 'shared/ui/table-skeleton';
import CardCreateDialog from './card-create-dialog';
import CardUpdateDialog from './card-update-dialog';
import { CardFreezeRequestsTab, CardUnfreezeRequestsTab } from './card-status-requests-tab';
import CardDetailsDialog from './card-details-dialog';
import CardReplacementRequestCreateDialog from './card-replacement-request-create-dialog';
import CardListAdvancedSearch from './card-list-advanced-search';
import { validateDateRange, validateOptionalFilterText, } from 'shared/lib/validation/search-filters';
const buildSkeletonCards = () => Array.from({ length: TABLE_SKELETON_ROW_COUNT }, (_, i) => ({ id: -(i + 1) }));
const createdRangeToIso = (d, endOfDay) => {
    if (!d)
        return null;
    const m = moment(d);
    return (endOfDay ? m.endOf('day') : m.startOf('day')).toISOString();
};
export const Cards = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [showCreate, setShowCreate] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [detailsCard, setDetailsCard] = useState(null);
    const [replacementCard, setReplacementCard] = useState(null);
    const [listPage, setListPage] = useState(0);
    const [listFirst, setListFirst] = useState(0);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [cardReferenceFilter, setCardReferenceFilter] = useState('');
    const [customerReferenceFilter, setCustomerReferenceFilter] = useState('');
    const [cardHolderNameFilter, setCardHolderNameFilter] = useState('');
    const [productIdFilter, setProductIdFilter] = useState(null);
    const [currencyIdFilter, setCurrencyIdFilter] = useState(null);
    const [cardTypeFilter, setCardTypeFilter] = useState(null);
    const [cardFormFilter, setCardFormFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [commissionGroupFilter, setCommissionGroupFilter] = useState('');
    const [rewardGroupFilter, setRewardGroupFilter] = useState('');
    const [pricingGroupFilter, setPricingGroupFilter] = useState('');
    const [permissionFlagsFilter, setPermissionFlagsFilter] = useState([]);
    const [interchangeScopeFilter, setInterchangeScopeFilter] = useState(null);
    const [authorizationRequirementFilter, setAuthorizationRequirementFilter] = useState(null);
    const [usageRestrictionFilter, setUsageRestrictionFilter] = useState(null);
    const [createdFromFilter, setCreatedFromFilter] = useState(null);
    const [createdToFilter, setCreatedToFilter] = useState(null);
    const [appliedCardReference, setAppliedCardReference] = useState('');
    const [appliedCustomerReference, setAppliedCustomerReference] = useState('');
    const [appliedCardHolderName, setAppliedCardHolderName] = useState('');
    const [appliedProductId, setAppliedProductId] = useState(null);
    const [appliedCurrencyId, setAppliedCurrencyId] = useState(null);
    const [appliedCardType, setAppliedCardType] = useState(null);
    const [appliedCardForm, setAppliedCardForm] = useState(null);
    const [appliedStatus, setAppliedStatus] = useState(null);
    const [appliedCommissionGroup, setAppliedCommissionGroup] = useState('');
    const [appliedRewardGroup, setAppliedRewardGroup] = useState('');
    const [appliedPricingGroup, setAppliedPricingGroup] = useState('');
    const [appliedPermissionFlags, setAppliedPermissionFlags] = useState([]);
    const [appliedInterchangeScope, setAppliedInterchangeScope] = useState(null);
    const [appliedAuthorizationRequirement, setAppliedAuthorizationRequirement] = useState(null);
    const [appliedUsageRestriction, setAppliedUsageRestriction] = useState(null);
    const [appliedCreatedFrom, setAppliedCreatedFrom] = useState(null);
    const [appliedCreatedTo, setAppliedCreatedTo] = useState(null);
    const listParams = useMemo(() => {
        const params = {
            page: listPage,
            size: ROWS_PER_PAGE,
        };
        const cref = appliedCardReference.trim();
        const cust = appliedCustomerReference.trim();
        if (cref)
            params['cardReference.contains'] = cref;
        if (cust)
            params['customerReference.contains'] = cust;
        const holder = appliedCardHolderName.trim();
        if (holder)
            params['cardHolderName.contains'] = holder;
        if (appliedProductId != null)
            params['productId.equals'] = appliedProductId;
        if (appliedCurrencyId != null)
            params['currencyId.equals'] = appliedCurrencyId;
        if (appliedCardType)
            params['cardType.equals'] = appliedCardType;
        if (appliedCardForm)
            params['cardForm.equals'] = appliedCardForm;
        if (appliedStatus)
            params['status.equals'] = appliedStatus;
        const comm = appliedCommissionGroup.trim();
        if (comm)
            params['commissionGroupCode.equals'] = comm;
        const rew = appliedRewardGroup.trim();
        if (rew)
            params['rewardGroupCode.equals'] = rew;
        const price = appliedPricingGroup.trim();
        if (price)
            params['pricingGroupCode.equals'] = price;
        for (const flag of appliedPermissionFlags) {
            params[`${flag}.equals`] = true;
        }
        if (appliedInterchangeScope)
            params['interchangeScope.equals'] = appliedInterchangeScope;
        if (appliedAuthorizationRequirement)
            params['authorizationRequirement.equals'] = appliedAuthorizationRequirement;
        if (appliedUsageRestriction)
            params['usageRestriction.equals'] = appliedUsageRestriction;
        const fromIso = createdRangeToIso(appliedCreatedFrom, false);
        if (fromIso)
            params['createdAt.greaterThanOrEqual'] = fromIso;
        const toIso = createdRangeToIso(appliedCreatedTo, true);
        if (toIso)
            params['createdAt.lessThanOrEqual'] = toIso;
        return params;
    }, [
        listPage,
        appliedCardReference,
        appliedCustomerReference,
        appliedCardHolderName,
        appliedProductId,
        appliedCurrencyId,
        appliedCardType,
        appliedCardForm,
        appliedStatus,
        appliedCommissionGroup,
        appliedRewardGroup,
        appliedPricingGroup,
        appliedPermissionFlags,
        appliedInterchangeScope,
        appliedAuthorizationRequirement,
        appliedUsageRestriction,
        appliedCreatedFrom,
        appliedCreatedTo,
    ]);
    const { data, isPending, isFetching, isError, refetch, } = useQuery({
        queryKey: cardQueryKeys.list(listParams),
        queryFn: () => fetchCardsList(listParams),
        placeholderData: keepPreviousData,
    });
    const cards = data?.cards ?? [];
    const totalItems = data?.totalItems ?? 0;
    const showSkeleton = isPending && data === undefined;
    const deleteMutation = useMutation({
        mutationFn: deleteCardRequest,
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lists() });
        },
    });
    const invalidateCardList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lists() });
    }, [queryClient]);
    const cardEntityLabel = translate('form.card');
    const cardListTranslations = {
        confirmYes: translate('entity.action.yes'),
        confirmNo: translate('entity.action.no'),
        deleteTitle: translate('entity.delete.title'),
        cardEntity: cardEntityLabel,
        createCard: translate('entity.action.create', { entity: cardEntityLabel }),
        cms: translate('form.cms'),
        tableEmpty: translate('general.dtEmptymessage'),
        cardReference: translate('form.cardReference'),
        maskedPan: translate('form.maskedPan'),
        cardHolderName: translate('form.cardHolderName'),
        customerReference: translate('form.customerReference'),
        productName: translate('form.productName'),
        currency: translate('form.currency'),
        cardType: translate('form.cardType'),
        status: translate('form.status'),
        issueDate: translate('form.issueDate'),
        actions: translate('general.actions'),
        cardFreezeTab: translate('form.cardFreezeRequests'),
        cardUnfreezeTab: translate('form.cardUnfreezeRequests'),
        cardReplacementRequest: translate('form.cardReplacementRequest'),
        search: translate('entity.action.search'),
        clear: translate('general.reset'),
        searchByCardReference: translate('entity.action.searchBy', { search: translate('form.cardReference') }),
        searchByCustomerReference: translate('entity.action.searchBy', {
            search: translate('form.customerReference'),
        }),
        advancedToggle: translate('general.advancedSearch.toggle'),
    };
    const cardRefSearchError = validateOptionalFilterText(cardReferenceFilter);
    const customerRefSearchError = validateOptionalFilterText(customerReferenceFilter);
    const cardHolderSearchError = validateOptionalFilterText(cardHolderNameFilter);
    const commissionErr = validateOptionalFilterText(commissionGroupFilter);
    const rewardErr = validateOptionalFilterText(rewardGroupFilter);
    const pricingErr = validateOptionalFilterText(pricingGroupFilter);
    const createdDateRangeError = validateDateRange(createdFromFilter, createdToFilter);
    const searchDisabled = !!(cardRefSearchError ||
        customerRefSearchError ||
        cardHolderSearchError ||
        commissionErr ||
        rewardErr ||
        pricingErr ||
        createdDateRangeError);
    const handleSearch = () => {
        if (searchDisabled)
            return;
        setListPage(0);
        setListFirst(0);
        setAppliedCardReference(cardReferenceFilter.trim());
        setAppliedCustomerReference(customerReferenceFilter.trim());
        setAppliedCardHolderName(cardHolderNameFilter.trim());
        setAppliedProductId(productIdFilter);
        setAppliedCurrencyId(currencyIdFilter);
        setAppliedCardType(cardTypeFilter);
        setAppliedCardForm(cardFormFilter);
        setAppliedStatus(statusFilter);
        setAppliedCommissionGroup(commissionGroupFilter.trim());
        setAppliedRewardGroup(rewardGroupFilter.trim());
        setAppliedPricingGroup(pricingGroupFilter.trim());
        setAppliedPermissionFlags([...permissionFlagsFilter]);
        setAppliedInterchangeScope(interchangeScopeFilter);
        setAppliedAuthorizationRequirement(authorizationRequirementFilter);
        setAppliedUsageRestriction(usageRestrictionFilter);
        setAppliedCreatedFrom(createdFromFilter);
        setAppliedCreatedTo(createdToFilter);
    };
    const handleClear = () => {
        setCardReferenceFilter('');
        setCustomerReferenceFilter('');
        setCardHolderNameFilter('');
        setProductIdFilter(null);
        setCurrencyIdFilter(null);
        setCardTypeFilter(null);
        setCardFormFilter(null);
        setStatusFilter(null);
        setCommissionGroupFilter('');
        setRewardGroupFilter('');
        setPricingGroupFilter('');
        setPermissionFlagsFilter([]);
        setInterchangeScopeFilter(null);
        setAuthorizationRequirementFilter(null);
        setUsageRestrictionFilter(null);
        setCreatedFromFilter(null);
        setCreatedToFilter(null);
        setAppliedCardReference('');
        setAppliedCustomerReference('');
        setAppliedCardHolderName('');
        setAppliedProductId(null);
        setAppliedCurrencyId(null);
        setAppliedCardType(null);
        setAppliedCardForm(null);
        setAppliedStatus(null);
        setAppliedCommissionGroup('');
        setAppliedRewardGroup('');
        setAppliedPricingGroup('');
        setAppliedPermissionFlags([]);
        setAppliedInterchangeScope(null);
        setAppliedAuthorizationRequirement(null);
        setAppliedUsageRestriction(null);
        setAppliedCreatedFrom(null);
        setAppliedCreatedTo(null);
        setListPage(0);
        setListFirst(0);
    };
    const onDelete = (row, event) => {
        confirmPopup({
            acceptLabel: cardListTranslations.confirmYes,
            rejectLabel: cardListTranslations.confirmNo,
            target: event.currentTarget,
            message: cardListTranslations.deleteTitle,
            icon: 'pi pi-exclamation-triangle',
            accept() {
                if (row.id != null) {
                    deleteMutation.mutate(row.id);
                }
            },
        });
    };
    const actionBodyTemplate = (rowData) => isTableSkeletonRow(rowData) ? (tableActionButtonsSkeleton({ buttonCount: 4 })) : (_jsxs(PRow, { children: [_jsx(Button, { icon: "pi pi-eye", className: "p-mx-1", onClick: () => rowData.id != null && navigate(`./${rowData.id}`, { relative: 'path' }) }), _jsx(Button, { icon: "pi pi-refresh", type: "button", className: "p-button-primary p-mx-1", onClick: () => {
                    if (rowData.id != null && !isTableSkeletonRow(rowData)) {
                        setReplacementCard(rowData);
                    }
                }, title: cardListTranslations.cardReplacementRequest, "aria-label": cardListTranslations.cardReplacementRequest })] }));
    const tableValue = showSkeleton ? buildSkeletonCards() : cards.slice();
    const getCardStatusSeverity = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'REQUESTED':
            case 'CREATED':
            case 'ISSUED':
            case 'PRINTED':
            case 'PIN_GENERATED':
                return 'info';
            case 'FROZEN':
            case 'SUSPENDED':
                return 'warning';
            case 'DEACTIVATED':
            case 'HOTLISTED':
            case 'EXPIRED':
            case 'CLOSED':
                return 'danger';
            case 'REPLACED':
                return 'secondary';
            default:
                return 'secondary';
        }
    };
    const header = (_jsxs("div", { style: { width: '100%' }, children: [_jsxs("div", { className: "p-d-flex p-jc-between p-ai-end p-flex-wrap", style: { gap: '0.5rem' }, children: [_jsxs("div", { className: "p-d-flex p-ai-end p-flex-wrap", style: { gap: '0.5rem' }, children: [_jsx(Button, { type: "button", icon: "pi pi-plus", label: cardListTranslations.createCard, onClick: () => setShowCreate(true) }), _jsx(Button, { type: "button", icon: showAdvanced ? 'pi pi-chevron-up' : 'pi pi-chevron-down', className: "p-button-outlined", label: cardListTranslations.advancedToggle, onClick: () => setShowAdvanced(s => !s) }), isFetching && !showSkeleton ? (_jsx("span", { className: "p-text-secondary p-d-flex p-ai-center", style: { fontSize: '0.875rem' }, children: translate('form.loading') })) : null] }), _jsxs("div", { className: "p-d-flex p-ai-end", style: { gap: '0.5rem' }, children: [_jsx(Button, { type: "button", icon: "pi pi-search", label: cardListTranslations.search, onClick: handleSearch, disabled: searchDisabled }), _jsx(Button, { type: "button", icon: "pi pi-times", className: "p-button-secondary", label: cardListTranslations.clear, onClick: handleClear })] })] }), showAdvanced ? (_jsx(CardListAdvancedSearch, { cardHolderNameFilter: cardHolderNameFilter, setCardHolderNameFilter: setCardHolderNameFilter, productIdFilter: productIdFilter, setProductIdFilter: setProductIdFilter, currencyIdFilter: currencyIdFilter, setCurrencyIdFilter: setCurrencyIdFilter, cardTypeFilter: cardTypeFilter, setCardTypeFilter: setCardTypeFilter, cardFormFilter: cardFormFilter, setCardFormFilter: setCardFormFilter, statusFilter: statusFilter, setStatusFilter: setStatusFilter, commissionGroupFilter: commissionGroupFilter, setCommissionGroupFilter: setCommissionGroupFilter, rewardGroupFilter: rewardGroupFilter, setRewardGroupFilter: setRewardGroupFilter, pricingGroupFilter: pricingGroupFilter, setPricingGroupFilter: setPricingGroupFilter, permissionFlagsFilter: permissionFlagsFilter, setPermissionFlagsFilter: setPermissionFlagsFilter, interchangeScopeFilter: interchangeScopeFilter, setInterchangeScopeFilter: setInterchangeScopeFilter, authorizationRequirementFilter: authorizationRequirementFilter, setAuthorizationRequirementFilter: setAuthorizationRequirementFilter, usageRestrictionFilter: usageRestrictionFilter, setUsageRestrictionFilter: setUsageRestrictionFilter, createdFromFilter: createdFromFilter, setCreatedFromFilter: setCreatedFromFilter, createdToFilter: createdToFilter, setCreatedToFilter: setCreatedToFilter, cardHolderSearchError: cardHolderSearchError, commissionErr: commissionErr, rewardErr: rewardErr, pricingErr: pricingErr, createdDateRangeError: createdDateRangeError })) : null] }));
    return (_jsxs(_Fragment, { children: [isError ? (_jsxs("div", { className: "mb-3 d-flex align-items-center flex-wrap gap-2", children: [_jsx(Message, { severity: "error", className: "flex-grow-1", text: translate('error.internal_error') }), _jsx(Button, { type: "button", icon: "pi pi-refresh", className: "p-button-outlined", onClick: () => void refetch(), disabled: isFetching })] })) : null, _jsxs(PRow, { children: [_jsx(PCol, { col: "6", className: "align-start", children: _jsx(BreadCrumb, { home: { icon: 'pi pi-home', command: () => navigate('/', { replace: true }) }, model: [{ label: cardListTranslations.cms }, { label: cardListTranslations.cardEntity }] }) }), _jsx(PCol, { col: "12", className: "align-start h-full", children: _jsxs(TabView, { activeIndex: activeTabIndex, onTabChange: e => setActiveTabIndex(e.index), children: [_jsxs(TabPanel, { header: cardListTranslations.cardEntity, children: [_jsxs(DataTable, { dataKey: "id", lazy: true, header: header, value: tableValue, first: listFirst, paginator: !showSkeleton && totalItems > ROWS_PER_PAGE, rows: ROWS_PER_PAGE, totalRecords: totalItems, onPage: e => {
                                                setListFirst(e.first ?? 0);
                                                setListPage(e.page ?? 0);
                                            }, selectionMode: "single", isDataSelectable: e => !isTableSkeletonRow(e.data), resizableColumns: true, emptyMessage: cardListTranslations.tableEmpty, className: "mt-5", children: [_jsx(Column, { field: "cardReference", header: cardListTranslations.cardReference, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.cardReference ?? '') }), _jsx(Column, { field: "maskedPan", header: cardListTranslations.maskedPan, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.maskedPan ?? '') }), _jsx(Column, { field: "cardHolderName", header: cardListTranslations.cardHolderName, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.cardHolderName ?? '') }), _jsx(Column, { field: "customerReference", header: cardListTranslations.customerReference, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.customerReference ?? '') }), _jsx(Column, { field: "product.productName", header: cardListTranslations.productName, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.product?.productName ?? '') }), _jsx(Column, { field: "currency.code", header: cardListTranslations.currency, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.currency?.code ?? '') }), _jsx(Column, { field: "cardType", header: cardListTranslations.cardType, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.cardType ?? '') }), _jsx(Column, { field: "status", header: cardListTranslations.status, body: row => isTableSkeletonRow(row) ? (tableCellSkeleton()) : (_jsx(Tag, { value: row.status ?? '', severity: getCardStatusSeverity(row.status) })) }), _jsx(Column, { field: "issueDate", header: cardListTranslations.issueDate, body: row => isTableSkeletonRow(row) ? tableCellSkeleton() : (row.issueDate ?? '') }), _jsx(Column, { header: cardListTranslations.actions, body: actionBodyTemplate })] }), _jsx(Dialog, { contentStyle: { borderRadius: '10px', overflowX: 'visible', maxHeight: '90vh' }, showHeader: false, style: { width: '90vw', maxWidth: '720px' }, visible: showCreate, onHide: () => setShowCreate(false), children: _jsx(CardCreateDialog, { closeDialog: () => setShowCreate(false), onMutationSuccess: invalidateCardList }) }), _jsx(Dialog, { contentStyle: { borderRadius: '10px', overflowX: 'visible', maxHeight: '90vh' }, showHeader: false, style: { width: '90vw' }, visible: showDetailsDialog, onHide: () => setShowDetailsDialog(false), children: _jsx(CardDetailsDialog, { current: detailsCard, closeDialog: () => {
                                                    setShowDetailsDialog(false);
                                                    setDetailsCard(null);
                                                } }) }), _jsx(Dialog, { contentStyle: { borderRadius: '10px', overflowX: 'visible', maxHeight: '90vh' }, showHeader: false, style: { width: '90vw', maxWidth: '720px' }, visible: !!replacementCard, onHide: () => setReplacementCard(null), children: replacementCard ? (_jsx(CardReplacementRequestCreateDialog, { card: replacementCard, closeDialog: () => setReplacementCard(null), onCreated: invalidateCardList })) : null }), _jsx(Dialog, { contentStyle: { borderRadius: '10px', overflowX: 'visible', maxHeight: '90vh' }, showHeader: false, style: { width: '90vw', maxWidth: '720px' }, visible: !!editingCard, onHide: () => setEditingCard(null), children: editingCard ? (_jsx(CardUpdateDialog, { card: editingCard, closeDialog: () => setEditingCard(null), onMutationSuccess: () => {
                                                    invalidateCardList();
                                                    if (editingCard.id != null) {
                                                        void queryClient.invalidateQueries({
                                                            queryKey: cardQueryKeys.detail(editingCard.id),
                                                        });
                                                    }
                                                } })) : null })] }), _jsx(TabPanel, { header: cardListTranslations.cardFreezeTab, children: _jsx(CardFreezeRequestsTab, {}) }), _jsx(TabPanel, { header: cardListTranslations.cardUnfreezeTab, children: _jsx(CardUnfreezeRequestsTab, {}) })] }) })] })] }));
};
export default Cards;
